import { getLocaleTag, localeFormats } from '@/utils/locale';
import {
  AddressType,
  ContactInput,
  Country,
  GOODS_TYPE_CONFIG,
  GoodsType,
  InputDataType,
  OpenState,
  PaymentTypes,
  countries,
} from '@/config/constants';
import {
  AddressWithType,
  Contact,
  Good,
  Job,
  JobExtraRef,
  JobQuote,
  Stop,
} from '@/api/freedomQl/__generated__/graphql';
import {
  BookingInput,
  BookingSection,
  BookingStop,
  BookingTemplate,
  DistDesc,
  InputParams,
  IntegraAccount,
  IntegraStop,
  IntegraUser,
  Settings,
} from '@/api/integraLink/types';
import { addMinutes, formatDateTimeToIso } from './dateManipulation';
import {
  cardOutline,
  cashOutline,
  personCircleOutline,
  walletOutline,
} from 'ionicons/icons';
import {
  mapJobQuoteToQuotePayload,
  postValidateQuoteId,
} from '@/api/hasher/service';

import { Bitmap } from '@/config/types';
import { Capacitor } from '@capacitor/core';
import { Color } from 'jspdf-autotable';
import { InboundQuote } from '@/types/booking';
import { Keyboard } from '@capacitor/keyboard';
import config from '@/config';
import { getJobQuote } from '@/api/freedomQl';
import { isBrowser } from '@/services/plugins/device';
import { isDarkMode } from '@/services/theme';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { showErrorToast } from './toast';
import { t } from './i18n';
import { useStore } from '@/store';
import * as valid from './validators';

export const arrayEquals = (a: any[], b: any) => {
  return JSON.stringify(a) == JSON.stringify(b);
};

// Works out the distance in meters between a pair of locations (latLongs)
export const haversineFormula = (
  lat1?: number,
  lon1?: number,
  lat2?: number,
  lon2?: number,
): number | undefined => {
  if (!lat1 || !lon1 || !lat2 || !lon2) {
    return undefined;
  }

  const R = 6371e3; // metres
  const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const d = R * c; // in metres
  return d;
};

export const getPaymentTypeIcon = (paymentChar?: string | null) => {
  switch (paymentChar) {
    case PaymentTypes.ACCOUNT:
      return personCircleOutline;

    case PaymentTypes.CARD:
      return cardOutline;

    case PaymentTypes.CASH:
      return cashOutline;

    default:
      return walletOutline;
  }
};

export const integraUserToContact = (user: IntegraUser): Contact => {
  return {
    reference: user.contactRef?.toString(),
    accountRef: user.accountRef,
    contactName: user.name,
    telephone: user.telephone,
    mobile: user.mobile,
    email: user.email,
  } as Contact;
};

export const getIntegraAccount = (
  accountRef: string,
): IntegraAccount | undefined =>
  useStore().integraProfile.accounts.find(
    (account) => account.accountRef === accountRef,
  );

// Converts a nationally formatted number into E.164, the only form Cognito and
// IntegraLink accept. Parsing against the selected country strips that
// country's trunk prefix and whatever grouping punctuation its convention uses.
export const formatMobileNumber = (
  mobile?: string,
  countryCode?: CountryCode,
): string | undefined => {
  if (!mobile || !countryCode) return undefined;

  return parsePhoneNumberFromString(mobile, countryCode)?.number;
};

export const scrollInView = (id: string) => {
  if (Capacitor.isNativePlatform()) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }
};

export const toNum = (s?: string | number | null) => {
  return s !== undefined && s !== null && s !== '' ? +s : undefined;
};

export const getGoodsInfoKeyByType = (
  type: 'phone' | 'mobile' | 'mail',
): keyof Good | undefined => {
  const settings = useStore().integraProfile.settings;
  if (!settings) return;

  const match = (Object.keys(settings) as (keyof Settings)[]).find((key) => {
    const value = settings[key];
    return (
      key.toLowerCase().startsWith('goodsinfo') &&
      typeof value === 'string' &&
      value.toLowerCase().includes(type.toLowerCase())
    );
  });

  if (!match) return;

  // Convert e.g. goodsInfo2Desc → info2
  return match
    .replace('goodsInfo', 'info')
    .replace('Desc', '')
    .toLowerCase() as keyof Good;
};

export const getGoodsInfoKeyMap = () => {
  const phoneKey = getGoodsInfoKeyByType('phone') ?? 'info1';
  const mobileKey = getGoodsInfoKeyByType('mobile') ?? 'info2';
  const emailKey = getGoodsInfoKeyByType('mail') ?? 'info3';

  return { phoneKey, mobileKey, emailKey };
};

export const getApiCountries = (apiCountries?: string | null) => {
  // 2 = all
  // 1 = non-UK only
  // 0 = UK only

  switch (apiCountries) {
    // TODO - handle 1 (non-UK only)
    case '1':
    case '2':
      return undefined;

    default:
      return 'GB';
  }
};

export const isMultipleOf = (val: number, multipleOf: number): boolean => {
  return val % multipleOf === 0;
};

export const hideKeyboard = async () => {
  if (!(await isBrowser())) {
    await Keyboard.hide();
  }
};

export const getGoodsLabel = (goodsType?: GoodsType): string => {
  const config = goodsType ? GOODS_TYPE_CONFIG[goodsType] : null;
  return t(config?.singular ?? 'base.goodsTypes.good');
};

export const getGoodsQtyLabel = (
  count: number,
  goodsType?: GoodsType,
): string => {
  const config = goodsType ? GOODS_TYPE_CONFIG[goodsType] : null;
  return t(config?.plural ?? 'base.goodsTypes.goods', { count });
};

export const getGoodsCartonsLabel = (count: number, goodsType?: GoodsType) =>
  t(goodsType === GoodsType.PASSENGER ? 'base.bags' : 'base.cartons', {
    count,
  });

export const getDynamicColSize = (length: number) => {
  if (length === 1) return '6'; // Single item - 50% width, centered
  return isMultipleOf(length, 3) ? '4' : isMultipleOf(length, 2) ? '6' : '4';
};

export const insertBeforeLastOccurrence = (
  strToSearch: string,
  strToFind: string,
  strToInsert: string,
) => {
  const n = strToSearch.lastIndexOf(strToFind);
  if (n < 0) return strToSearch;
  return strToSearch.substring(0, n) + strToInsert + strToSearch.substring(n);
};

export const getFieldArrayInputName = (dataName: string, index: number) =>
  insertBeforeLastOccurrence(dataName, '.', `[${index}]`);

export const isFieldArrayInput = (inputName: string) => inputName.includes('.');

export const isContactInput = (inputName: string) =>
  [
    ContactInput.NAME,
    ContactInput.PHONE,
    ContactInput.MOBILE,
    ContactInput.EMAIL,
  ].includes(inputName as ContactInput);

export const isAsDirectedAddress = (address?: AddressWithType) =>
  address?.typeCode === AddressType.AS_DIRECTED;

export const isAsDirectedStop = (stop?: BookingStop) =>
  isAsDirectedAddress(stop?.address);

export const getParamsTooltip = (params?: InputParams) =>
  params?.tooltip?.[0].label ?? '';

export const sortOrderCompare = (a: any, b: any, reverse = false): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  return reverse ? (a < b ? 1 : a > b ? -1 : 0) : a < b ? -1 : a > b ? 1 : 0;
};

// A map icon in the variant for the current palette
export const getPaletteBitmap = (icon: {
  bitmap: Bitmap;
  darkBitmap?: Bitmap;
}): Bitmap => (isDarkMode.value && icon.darkBitmap) || icon.bitmap;

export const getStopSvgIcon = (index: number, total: number): string => {
  const { start, via, end, location } = config.app.hereMaps.icons;

  let svg: Bitmap;
  if (total === 1) svg = getPaletteBitmap(location);
  else if (index === 0) svg = start.bitmap;
  else if (index === total - 1) svg = end.bitmap;
  else svg = via.bitmap;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    svg as string,
  )}`;
};

// An as-directed stop is time spent at the stop before it rather than a place
// of its own, so it takes no part in the start/via/end sequence and has no
// icon of its own.
const isRouteStop = (stop?: BookingStop) => !isAsDirectedStop(stop);

export const getRouteStopIcon = (
  stops: BookingStop[],
  index: number,
): string | undefined => {
  if (!isRouteStop(stops[index])) return undefined;

  const position = stops.slice(0, index).filter(isRouteStop).length;

  return getStopSvgIcon(position, stops.filter(isRouteStop).length);
};

export const getLastElements = <T extends any[]>(arr: T, count?: number): T =>
  count ? (arr.slice(-count) as T) : arr;

export const outputAddressWithPostcode = (address: AddressWithType) => {
  if (isAsDirectedAddress(address)) {
    return t('form.stops.asDirectedLabel');
  }

  let addressString = address.addressName;
  if (!addressString) {
    addressString = address.address1;
  } else if (addressString.length < 5) {
    addressString += ` ${address.address1}`;
  }
  return `${addressString} - ${address?.postcode}`;
};

export const getDefaultCountry = (): Country => {
  return (
    countries.find((c) => c.code === config.app.defaultCountryCode) ??
    countries[0]
  );
};

// The profile carries the system's base currency, so it is the right code for
// anything priced outside a quote. The brand's own code is only a stand-in for
// before a profile has been loaded.
export const getSystemCurrencyCode = (): string =>
  useStore().integraProfile.settings?.currencyCode ||
  config.app.fallbackCurrencyCode;

// Matches countries on name, ISO code or dial code, so "united", "us" and "+1"
// all lead to the United States. Dial codes are compared digits-only and by
// prefix, because they share leading digits and may be typed with or without
// the leading plus.
export const filterCountries = (query: string): Country[] => {
  const search = query.trim().toLowerCase();
  if (!search) return countries;

  const digits = search.replace(/\D/g, '');

  return countries.filter(
    (country) =>
      country.name.toLowerCase().includes(search) ||
      country.code.toLowerCase() === search ||
      (!!digits && country.dialCode.replace(/\D/g, '').startsWith(digits)),
  );
};

export const toValidIndex = (val: unknown): number => {
  const n = Number(val);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
};

export const handlePreSelectedBookingTemplate = async (
  bookingTemplates: BookingTemplate[],
  templateId: string | undefined,
  startBookingFunction: (template: BookingTemplate) => void,
) => {
  const preSelectedTemplate = bookingTemplates.find((t) => t.id === templateId);

  if (preSelectedTemplate) {
    startBookingFunction(preSelectedTemplate);
  } else {
    useStore().clearBookingValues();
    await showErrorToast(t('toast.unableToFindBookingTemplate'));
  }
};

export const parseMobileNumber = (
  mobile?: string,
): { country?: Country; number?: string } => {
  if (!mobile) return { country: undefined, number: undefined };

  const defaultCountry = getDefaultCountry();
  const parsed = parsePhoneNumberFromString(mobile, defaultCountry.code);

  if (parsed?.isValid()) {
    const { nationalNumber, country } = parsed;
    const foundCountry =
      countries.find((c) => c.code === country) ?? defaultCountry;
    return {
      country: foundCountry,
      number: nationalNumber,
    };
  }

  return { country: defaultCountry, number: mobile };
};

// A passenger good holds its passenger in code2 (the contact reference),
// description and the goods info fields.
const getPassengerFields = (contact?: Contact): Partial<Good> => {
  const { phoneKey, mobileKey, emailKey } = getGoodsInfoKeyMap();

  return {
    code2: contact?.reference,
    description: contact?.contactName,
    [phoneKey]: contact?.telephone,
    [mobileKey]: contact?.mobile,
    [emailKey]: contact?.email,
  };
};

// Without a contact, the good's passenger fields are cleared.
export const setGoodPassenger = (good: Good, contact?: Contact): Good => ({
  ...good,
  ...getPassengerFields(contact),
});

export const hasPassengerDetails = (good?: Good): boolean =>
  !!good &&
  (Object.keys(getPassengerFields()) as (keyof Good)[]).some(
    (key) => good[key],
  );

export const getContactFromPassenger = (good?: Good): Contact | undefined => {
  if (!good || !good.code2) return undefined;

  const { phoneKey, mobileKey, emailKey } = getGoodsInfoKeyMap();

  return {
    reference: good.code2,
    contactName: good.description,
    telephone: good[phoneKey],
    mobile: good[mobileKey],
    email: good[emailKey],
  } as Contact | undefined;
};

export const getCssVar = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export const getRgbArray = (rgbString: string): Color =>
  rgbString
    .split(',')
    .map((rgb) => Number(rgb.trim()))
    .filter((n) => !isNaN(n)) as Color;

// Durations from Freedom, such as a quote's journey time or a job's estimate,
// are in its timeDesc unit, while the arrive-by pickup and the displayed
// duration both work in minutes.
export const freedomDurationToMins = (duration?: number | null) =>
  duration != null && useStore().integraProfile.settings?.timeDesc === 'Hours'
    ? Math.round(duration * 60)
    : (duration ?? undefined);

export const getArrivalTimeFromPickup = (
  currentEarliest?: string,
  includeBuffer = true,
) => {
  if (!currentEarliest) {
    return '';
  }

  const date = new Date(currentEarliest);

  if (isNaN(date.getTime())) {
    return '';
  }

  let arrivalTime = addMinutes(
    date,
    useStore().activeBooking.journeyDurationMins ?? 0,
  );

  if (includeBuffer) {
    arrivalTime = addMinutes(
      arrivalTime,
      config.app.bookingForm.arrivalPickupBufferMins,
    );
  }

  return formatDateTimeToIso(arrivalTime);
};

export const getPickupTimeFromArrival = (arrivalDt: string) => {
  const arrivalDate = new Date(arrivalDt);
  if (isNaN(arrivalDate.getTime())) return arrivalDt;

  const journeyDurationMins = useStore().activeBooking.journeyDurationMins ?? 0;
  const bufferMins = config.app.bookingForm.arrivalPickupBufferMins ?? 0;

  const pickupTime = addMinutes(
    arrivalDate,
    -(journeyDurationMins + bufferMins),
  );
  return formatDateTimeToIso(pickupTime);
};

// Freedom owns which currency a price is in and how much it is; the locale only
// decides separators and symbol placement, so it follows the reader the same way
// dates and distances do. The currency code is what keeps the value honest — it
// comes from the quote or the system, never from the locale.
//
// narrowSymbol keeps the symbol recognisable: the default renders a currency
// outside its home locale as "GBP 1,234.50" or "1 234,50 £GB".
export const formatPrice = (currency: string, price: number) => {
  return Intl.NumberFormat(getLocaleTag(), {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }).format(price);
};

// Below these, the smaller unit reads better: a kilometre or a tenth of a mile
// stated in metres or feet is a number nobody can picture.
const METRES_PER_KM = 1000;
const METRES_PER_MILE = 1609.344;
const METRES_PER_FOOT = 0.3048;
const IMPERIAL_SHORT_LIMIT = METRES_PER_MILE / 10;

// Distances arrive in metres whichever provider supplied them, so the unit
// shown is the locale's rather than the data's. The unit name lives in the
// message so it can be translated; only the number is formatted here, since
// Intl's own unit formatting needs a newer WebKit than the app's minimum iOS.
export const formatDistance = (
  metres: number,
): { distance: string; key: string } => {
  const { distanceUnits } = localeFormats.value;
  const imperial = distanceUnits === 'imperial';

  const short = imperial
    ? metres < IMPERIAL_SHORT_LIMIT
    : metres < METRES_PER_KM;

  const value = short
    ? metres / (imperial ? METRES_PER_FOOT : 1)
    : metres / (imperial ? METRES_PER_MILE : METRES_PER_KM);

  const key = imperial
    ? short
      ? 'feetAway'
      : 'milesAway'
    : short
      ? 'metresAway'
      : 'kilometresAway';

  return {
    distance: Intl.NumberFormat(getLocaleTag(), {
      maximumFractionDigits: short ? 0 : 1,
    }).format(value),
    key: `base.distance.${key}`,
  };
};

// A quoted journey distance is in the unit the system's distDesc names. The
// profile persists between sign-ins, so a value from before logon normalised
// the field can still be read; anything but kilometres reads as miles, as
// logon itself resolves it.
export const formatJourneyDistance = (
  value: number,
  distDesc?: DistDesc,
): string => {
  const distance = Intl.NumberFormat(getLocaleTag(), {
    maximumFractionDigits: 1,
  }).format(value);

  return t(
    distDesc === 'Kilometres'
      ? 'base.distance.kilometres'
      : 'base.distance.miles',
    { distance },
    value,
  );
};

export const isJobFlagInput = (input: BookingInput) =>
  /^flag\d+$/i.test(input.dataName);

export const isGoodsFlagInput = (input: BookingInput) =>
  /^goods.flag\d+$/i.test(input.dataName);

// Job flags are encoded as a string of "0"/"1" characters carrying no flag
// details, only which are set: a flag's position in the string is its
// flagId - 1.
export const isJobFlagSet = (
  jobFlags: string | null | undefined,
  flagId?: number,
) => !!jobFlags && flagId !== undefined && jobFlags.charAt(flagId - 1) === '1';

const isIntegraStop = (stop: Stop | IntegraStop): stop is IntegraStop =>
  'addr' in stop;

export const formatStopsSummary = (
  stops: (Stop | IntegraStop)[],
  includeLocality = true,
) =>
  stops
    .map((stop) => {
      if (isIntegraStop(stop)) {
        const showExtra =
          includeLocality && !stop.asDirected && stop.addr?.add3;
        return `${stop.addr?.postcode ?? stop.addr?.name ?? stop.addr?.add1}${
          showExtra ? ` (${stop.addr?.add3})` : ''
        }`;
      }

      const showExtra = includeLocality && !stop.asDirected && stop.address3;
      return `${stop.postcode ?? stop.addressName ?? stop.address1}${
        showExtra ? ` (${stop.address3})` : ''
      }`;
    })
    .join(' → ');

export const isHtml = (value: string | undefined): boolean => {
  if (!value) return false;
  return /<[a-z][\s\S]*>/i.test(value);
};

export const estimateBookingSectionHeight = (
  section: BookingSection,
): number => {
  switch (section.openState) {
    case OpenState.ALWAYS:
    case OpenState.OPEN:
      return 200;

    case OpenState.DEFAULT:
    case OpenState.NEVER:
    default:
      return 100;
  }
};

export const getInputMode = (type: InputDataType) => {
  switch (type) {
    case InputDataType.NUMBER:
    case InputDataType.CURRENCY:
      return 'decimal';
    case InputDataType.INTEGER:
      return 'numeric';
    case InputDataType.TEL:
      return 'tel';
    case InputDataType.EMAIL:
      return 'email';
    default:
      return undefined;
  }
};

export const toCoordinate = (value: number | string | undefined): number =>
  round(toNum(value), 6) ?? 0;

// Which postcode/ZIP format to expect, per country. A country with no entry
// here has no known format, so nothing is treated as a postcode for it.
const postcodeFormats: Partial<Record<CountryCode, RegExp>> = {
  GB: valid.postcode,
  US: valid.usZipCode,
};

export const isPossiblePostcode = (
  query: string,
  countryCode?: CountryCode,
): boolean => {
  const format = postcodeFormats[countryCode ?? getDefaultCountry().code];

  return format ? format.test(query.trim().toUpperCase()) : false;
};

export const extractIataCode = (name: string): string | undefined =>
  // Example string: Gatwick Airport (LGW)
  valid.iataCode.exec(name)?.[1];

export const mapJobQuoteToJob = (quote: JobQuote): Partial<Job> => ({
  pickupDt: quote.pickupDt,
  tariff: quote.tariff,
  prebook: quote.prebook,
  waitReturn: quote.waitReturn,
  webInstructions: quote.webInstructions,
  webInstructionsMaxLength: quote.webInstructionsMaxLength,
  mop: quote.mop,
  contactRef: toNum(quote.contactRef),
  contactName: quote.contactName,
  accountDept: quote.accountDept,
  otherRef: quote.otherRef,
  jobFlags: quote.jobFlags,

  stops: quote.jobQuoteStops.map((qs) => ({
    reference: qs.reference,
    dropOrder: qs.dropOrder,
    addressName: qs.addressName,
    address1: qs.address1,
    address2: qs.address2,
    address3: qs.address3,
    address4: qs.address4,
    postcode: qs.postcode,
    country: qs.country,
    countryCode: qs.countryCode,
    lat: qs.lat,
    long: qs.long,
    gridX: qs.gridX,
    gridY: qs.gridY,
    notes: qs.notes,
  })) as Stop[],

  jobExtraRefs: quote.jobQuoteExtraRefs.map((qer) => ({
    referenceValue: qer.referenceValue,
    accountExtraRefRef: qer.accountextrarefRef,
  })) as JobExtraRef[],

  goodsQty: 1,
  goods: [{}] as Good[],
});

export const round = (
  value: number | null | undefined,
  dp: number,
): number | null => {
  if (value === null || value === undefined) return null;
  return Number(value.toFixed(dp));
};

export const parseQuoteId = (q: string): InboundQuote | null => {
  // Decoded: <quoteRef>:<quoteHash>
  const decoded = atob(q);
  const [quoteRefPart, quoteHash] = decoded.split(':');

  const quoteRef = parseInt(quoteRefPart, 10);
  if (isNaN(quoteRef) || !quoteHash) return null;

  return { quoteRef, quoteHash };
};

export const getJobFromInboundQuote = async (
  inboundQuote?: InboundQuote | null,
): Promise<Job | undefined> => {
  if (!inboundQuote) return;

  const { quoteRef, quoteHash } = inboundQuote;

  const jobQuote = await getJobQuote(quoteRef);
  if (!jobQuote) return;

  const quote = mapJobQuoteToQuotePayload(jobQuote);

  const validateRes = await postValidateQuoteId({
    quoteId: quoteHash,
    quote,
  });

  if (!validateRes.valid) {
    throw new Error(validateRes.reason ?? 'Hasher validation failed');
  }

  return mapJobQuoteToJob(jobQuote) as Job;
};
